import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Download as DownloadIcon,
  FolderZip as ZipIcon
} from '@mui/icons-material';
import { downloadAsZip } from '../../services/downloadService';

/**
 * Reusable Download Button Component
 *
 * Downloads files as ZIP for various resource types (wireframes, designs, etc.)
 *
 * @param {string} resourceType - Type of resource to download (wireframes, designs, etc.)
 * @param {string} projectId - Project ID
 * @param {string} label - Button label (default: "Download All")
 * @param {string} variant - MUI Button variant (default: "outlined")
 * @param {string} color - MUI Button color (default: "primary")
 * @param {string} size - MUI Button size (default: "medium")
 * @param {boolean} fullWidth - Whether button should be full width
 * @param {boolean} showIcon - Whether to show download icon (default: true)
 * @param {boolean} disabled - Whether button is disabled
 * @param {function} onSuccess - Callback on successful download
 * @param {function} onError - Callback on download error
 * @param {object} sx - Additional MUI sx styles
 */
const DownloadButton = ({
  resourceType,
  projectId,
  label = 'Download All',
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  showIcon = true,
  disabled = false,
  onSuccess,
  onError,
  sx = {}
}) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleDownload = async () => {
    if (!resourceType || !projectId) {
      console.error('DownloadButton: resourceType and projectId are required');
      return;
    }

    setLoading(true);

    try {
      const result = await downloadAsZip(resourceType, projectId);

      setSnackbar({
        open: true,
        message: `Downloaded ${result.filename} successfully!`,
        severity: 'success'
      });

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      console.error('Download error:', error);

      setSnackbar({
        open: true,
        message: error.message || 'Download failed. Please try again.',
        severity: 'error'
      });

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const buttonLabel = loading ? 'Downloading...' : label;

  return (
    <>
      <Tooltip title={`Download all ${resourceType} as ZIP file`}>
        <span>
          <Button
            variant={variant}
            color={color}
            size={size}
            fullWidth={fullWidth}
            disabled={disabled || loading}
            onClick={handleDownload}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : showIcon ? (
                <ZipIcon />
              ) : null
            }
            endIcon={!loading && showIcon ? <DownloadIcon /> : null}
            sx={{
              textTransform: 'none',
              ...sx
            }}
          >
            {buttonLabel}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DownloadButton;
